'use client';

import type { CompactRatingCardConfig, CompactRatingResolved } from './types';
import { Icon } from '@/components/Icon';
import { useSubstitute } from '@/lib/render/RenderContextProvider';

const STAR_REPEAT_PLACEHOLDER = '{location.googleAverageRating}';

function parseNumericValue(value: string): number | null {
  const num = parseFloat(value.trim().replace(',', '.'));
  return isNaN(num) ? null : num;
}

function StatCard({
  card,
  sub,
}: {
  card: CompactRatingCardConfig;
  sub: (s: string) => string;
}) {
  const resolvedValue = sub(card.value);
  const resolvedText = sub(card.text);
  const isRating = card.value.includes(STAR_REPEAT_PLACEHOLDER);
  const numeric = parseNumericValue(resolvedValue);
  const showStars = isRating && numeric && numeric > 0 && card.icon;

  return (
    <div className="row-span-3 grid grid-rows-subgrid items-center gap-y-1.5">
      <div className="flex items-end justify-center">
        {showStars ? (
          <div className="flex h-[14px] items-center justify-center gap-[5px]">
            {(() => {
              const full = Math.floor(numeric);
              const hasHalf = numeric - full >= 0.25;
              return (
                <>
                  {Array.from({ length: full }, (_, j) => (
                    <Icon
                      key={j}
                      src={card.icon!}
                      size={14}
                      className="text-primary-dark"
                    />
                  ))}
                  {hasHalf && (
                    <div className="flex size-[14px] shrink-0 overflow-hidden">
                      <div className="flex w-1/2 shrink-0 overflow-hidden">
                        <Icon
                          src={card.icon!}
                          size={14}
                          className="text-primary-dark shrink-0"
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ) : (
          card.icon && (
            <Icon src={card.icon} size={28} className="text-primary-dark" />
          )
        )}
      </div>

      <p className="text-primary-dark m-0 text-center text-[32px] font-extrabold">
        {resolvedValue}
      </p>

      <p className="text-muted m-0 self-end text-center text-xs font-semibold">
        {resolvedText || ' '}
      </p>
    </div>
  );
}

export function CompactRatingComponent({
  resolved,
}: {
  resolved: CompactRatingResolved;
}) {
  const sub = useSubstitute();
  const cards = resolved.cards ?? [];

  if (cards.length === 0) {
    return null;
  }

  const cols =
    cards.length === 1
      ? 'grid-cols-1'
      : cards.length === 2
        ? 'grid-cols-2'
        : 'grid-cols-3';

  return (
    <div className="pt-4">
      <div
        className={`grid ${cols} gap-x-8 gap-y-1.5`}
        style={{ gridTemplateRows: 'auto auto auto' }}
      >
        {cards.map((card, i) => (
          <StatCard key={i} card={card} sub={sub} />
        ))}
      </div>
    </div>
  );
}
